import { Component, Input, computed, inject, signal } from '@angular/core';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { TranslateService } from '@ngx-translate/core';
import { StateChange } from 'ng-lazyload-image';
import { CartLine, CartService } from '../../services/cart.service';
import { Item } from '../../models/menu.model';
import { ItemStatus, VenueType } from '../enums';
import { ModifierComponent } from '../modifier/modifier.component';
import { ItemVariantsSheetComponent } from '../item-variants-sheet/item-variants-sheet.component';
import { ToastService } from '../../services/toast.service';
import { DataService } from '../../services/data.service';
import { ThemeService } from '../../services/theme.service';

@Component({
  selector: 'app-item',
  templateUrl: './item.component.html',
  styleUrl: './item.component.scss',
  standalone: false,
})
export class ItemComponent {
  @Input() item!: Item;
  @Input() subSectionId?: string;
  @Input() isOptionalSubSection?: boolean;
  @Input() isGridView = false;
  // Only set by search when this exact item occurrence is ambiguous (the same item also
  // shows up from a different section) - tells the guest which listing this one is.
  @Input() sectionBadge?: string;
  @Input() isCombo = false;
  // Small card for places outside the menu (cart recommendations): no description, name
  // clamped to two lines, no highlight styling, and + always opens the modifier sheet
  // (shown as add-only, never a quantity stepper).
  @Input() compact = false;
  @Input() alwaysShowImage = false;

  private cartService = inject(CartService);
  private bottomSheet = inject(MatBottomSheet);
  private toastService = inject(ToastService);
  private translateService = inject(TranslateService);
  private dataService = inject(DataService);

  public readonly placeholderImage = inject(ThemeService).placeholderImage;

  private failedImageUrl = signal<string | null>(null);

  public get hasImage(): boolean {
    return !!this.item.image_url && this.item.image_url !== this.failedImageUrl();
  }

  public get isImageHideable(): boolean {
    return !this.hasImage && !this.alwaysShowImage;
  }

  public onImageStateChange(event: StateChange): void {
    if (event.reason === 'loading-failed') {
      this.failedImageUrl.set(this.item.image_url);
    }
  }

  // Scoped to this card's own subsection - the same item can be listed in more than one
  // section/subsection, each getting its own cart line (see CartService.add/remove), so
  // summing by item id alone would show a combined count here that this card's own +/-
  // can never actually decrement to zero.
  public quantity = computed(() =>
    this.cartService.cartLines()
      .filter(line => line.item.id === this.item.id && (line.subSectionId || '') === (this.subSectionId || ''))
      .reduce((total, line) => total + line.quantity, 0));

  public get isHighlighted(): boolean {
    return this.item.highlight && !this.compact;
  }

  public get isUnavailable(): boolean {
    return this.item.status === ItemStatus.Unavailable;
  }

  public get isReadOnly(): boolean {
    return this.item.status === ItemStatus.ReadOnly || this.dataService.restaurant()?.type === VenueType.ReadOnly;
  }

  private get hasModifiers(): boolean {
    return this.item.modifier_groups.length > 0;
  }

  public increase(): void {
    if (this.isReadOnly) {
      return;
    }

    if (this.compact) {
      this.openModifierSheet();
      return;
    }

    if (this.isUnavailable) {
      this.toastService.warning(this.translateService.instant('COMMON.ITEM_UNAVAILABLE'));
      return;
    }

    if (this.hasModifiers) {
      // Already in the cart with at least one modifier combination chosen - let the
      // user pick which variant to bump (or configure a new one) instead of guessing.
      if (this.quantity() > 0) {
        this.openVariantsSheet();
      } else {
        this.openModifierSheet();
      }
      return;
    }

    this.cartService.add(this.item, [], this.subSectionId, this.isOptionalSubSection);
  }

  public decrease(): void {
    if (this.isReadOnly || this.quantity() === 0) {
      return;
    }

    const lines = this.matchingCartLines();

    // Only one line in the cart - unambiguous, decrement it directly.
    if (lines.length === 1) {
      this.cartService.decrementLine(lines[0].id);
    } else if (lines.length > 1) {
      // Several distinct lines (different modifiers, or - even without modifiers - a
      // different note on each) - ask which one to decrement.
      this.openVariantsSheet();
    }
  }

  // Read-only items open too - the sheet shows them view-only (no add controls).
  public openModifierSheet(): void {
    if (this.isUnavailable) {
      this.toastService.warning(this.translateService.instant('COMMON.ITEM_UNAVAILABLE'));
      return;
    }

    this.bottomSheet.open(ModifierComponent, {
      autoFocus: false,
      data: {
        item: this.item,
        subSectionId: this.subSectionId,
        isOptionalSubSection: this.isOptionalSubSection,
      },
    });
  }

  public openVariantsSheet(): void {
    if (this.isUnavailable) return;

    this.bottomSheet.open(ItemVariantsSheetComponent, {
      autoFocus: false,
      data: {
        item: this.item,
        subSectionId: this.subSectionId,
        isOptionalSubSection: this.isOptionalSubSection,
      },
    });
  }

  private matchingCartLines(): CartLine[] {
    return this.cartService.cartLines()
      .filter(line => line.item.id === this.item.id && (line.subSectionId || '') === (this.subSectionId || ''));
  }
}
